// src/components/atoms/Dropdown/Dropdown.tsx
import React, { useState, useRef, useEffect } from 'react';
import * as styles from './Dropdown.css';
import clsx from 'clsx';
import { Icon } from 'icons';

export interface DropdownOption {
  value: string;
  label: string;
}

export interface DropdownProps {
  /** 아이템 목록 */
  options?: DropdownOption[];
  /** 아무것도 선택되지 않았을 때 보여줄 텍스트 */
  placeholder?: string;
  /** 선택 값이 바뀔 때 호출되는 콜백 */
  onChange?: (value: string) => void;
  /** 비활성화 여부 */
  disabled?: boolean;
  className?: string;
  /** 외부에서 제어하는 선택값 (선택) */
  value?: string | null;
}

export const Dropdown: React.FC<DropdownProps> = ({
  options = [],
  placeholder = '선택해주세요.',
  onChange,
  disabled = false,
  className,
  value = undefined,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [selected, setSelected] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 전역 DOM MouseEvent 타입을 사용합니다.
    const handleClick = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => {
      document.removeEventListener('mousedown', handleClick);
    };
  }, []);

  const handleSelect = (val: string) => {
    // 외부 제어 모드가 아니면 내부 상태 업데이트
    if (typeof value === 'undefined') {
      setSelected(val);
    }
    setOpen(false);
    onChange?.(val);
  };

  const currentValue = typeof value === 'undefined' ? selected : value;
  const selectedLabel =
    options.find((o) => o.value === currentValue)?.label ?? placeholder;

  return (
    <div className={clsx(styles.wrapper, className)} ref={ref}>
      <button
        type="button"
        className={styles.button}
        onClick={() => setOpen((prev) => !prev)}
        disabled={disabled}
      >
        {selectedLabel}
        <Icon
          name="arrowDown"
          size={24}
          style={{
            position: 'absolute',
            right: 16,
            top: '50%',
            transform: open
              ? 'translateY(-50%) rotate(180deg)'
              : 'translateY(-50%) rotate(0deg)',
          }}
        />
      </button>

      <div className={clsx(styles.menu, open && styles.menuVisible)}>
        {options.length > 0 ? (
          options.map((opt) => (
            <div
              key={opt.value}
              className={styles.menuItem}
              data-selected={opt.value === selected ? true : undefined}
              onClick={() => handleSelect(opt.value)}
            >
              {opt.label}
            </div>
          ))
        ) : (
          <div className={styles.menuItem} style={{ cursor: 'default' }}>
            옵션이 없습니다
          </div>
        )}
      </div>
    </div>
  );
};